import { useEffect, useRef } from "react";

type PlotProps = {
  data: unknown[];
  layout: Record<string, unknown>;
  config?: Record<string, unknown>;
  title: string;
  description?: string;
  onPointClick?: (value: string | number | null) => void;
};

type PlotElement = HTMLDivElement & {
  on?: (event: string, handler: (event: { points?: Array<{ x?: unknown; y?: unknown; label?: unknown; customdata?: unknown }> }) => void) => void;
  removeAllListeners?: (event: string) => void;
};

export function Plot({ data, layout, config, title, description, onPointClick }: PlotProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const descriptionId = `${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-description`;

  useEffect(() => {
    let active = true;
    async function draw() {
      const Plotly = (await import("plotly.js-basic-dist-min")).default;
      if (!active || !ref.current) {
        return;
      }
      await Plotly.react(ref.current, data, {
        autosize: true,
        margin: { l: 150, r: 28, t: 28, b: 86 },
        paper_bgcolor: "rgba(0,0,0,0)",
        plot_bgcolor: "rgba(0,0,0,0)",
        font: { family: "Inter, ui-sans-serif, system-ui", color: "#1f2937" },
        xaxis: { automargin: true, gridcolor: "rgba(148, 163, 184, 0.22)", zerolinecolor: "rgba(15, 23, 42, 0.45)", title: { standoff: 14 } },
        yaxis: { automargin: true, gridcolor: "rgba(148, 163, 184, 0.22)", zerolinecolor: "rgba(15, 23, 42, 0.45)", title: { standoff: 16 } },
        ...layout
      }, {
        responsive: true,
        displaylogo: false,
        toImageButtonOptions: { format: "png", filename: title },
        ...config
      });
      const node = ref.current as PlotElement;
      node.removeAllListeners?.("plotly_click");
      if (onPointClick) {
        node.on?.("plotly_click", (event) => {
          const point = event.points?.[0];
          const value = point?.customdata ?? point?.label ?? point?.x ?? point?.y ?? null;
          onPointClick(typeof value === "string" || typeof value === "number" ? value : null);
        });
      }
    }
    draw();
    return () => {
      active = false;
    };
  }, [config, data, layout, onPointClick, title]);

  return (
    <>
      <div className="plot" ref={ref} role="img" aria-label={title} aria-describedby={description ? descriptionId : undefined} />
      {description ? <p id={descriptionId} className="sr-only">{description}</p> : null}
    </>
  );
}
